import type { IBuzzbridgeNetworkInvite } from '../types/global/buzzbridgeLoyalty.types';
import type { ICampaignReferralEarningsIfFriendHired } from '../types/global/campaignReferralEarnings.types';
import { ROUTES } from '../types/global/routes.types';
import { formatCurrency } from '../constants/currency';

export const PENDING_CAMPAIGN_REFERRAL_COPY_KEY = 'buzzbridge_pending_referral_copy_campaign_id';

export interface ICampaignReferralCopyDisplay {
    headline?: string;
    detail?: string;
    short?: string;
    perReferral?: string;
}

export function resolveCampaignReferralCopyDisplay(
    earnings: ICampaignReferralEarningsIfFriendHired,
): ICampaignReferralCopyDisplay | null {
    const copy = earnings.copy;
    if (copy?.headline || copy?.detail || copy?.short || copy?.per_referral) {
        return {
            headline: copy.headline?.trim() || undefined,
            detail: copy.detail?.trim() || undefined,
            short: copy.short?.trim() || undefined,
            perReferral: copy.per_referral?.trim() || undefined,
        };
    }
    return null;
}

export function formatCampaignReferralEarnAmount(earnings: ICampaignReferralEarningsIfFriendHired): string | null {
    if (earnings.cash_usd != null && Number.isFinite(earnings.cash_usd) && earnings.cash_usd > 0) {
        return formatCurrency(earnings.cash_usd);
    }
    if (earnings.cash_usd_cents != null && Number.isFinite(earnings.cash_usd_cents) && earnings.cash_usd_cents > 0) {
        return formatCurrency(earnings.cash_usd_cents / 100);
    }
    if (earnings.points != null && earnings.points > 0) {
        return `${new Intl.NumberFormat('en-US').format(earnings.points)} pts`;
    }
    return null;
}

export function isCampaignReferralEarnVisible(earnings?: ICampaignReferralEarningsIfFriendHired | null): boolean {
    if (!earnings?.enabled) return false;
    if (resolveCampaignReferralCopyDisplay(earnings)) return true;
    return Boolean(formatCampaignReferralEarnAmount(earnings));
}

export function buildCampaignReferralShareUrl(invite: IBuzzbridgeNetworkInvite, campaignId: string): string {
    if (typeof window === 'undefined') return invite.referral_link;

    const path = ROUTES.BUZZ_BRIDGE_DETAILS.replace(':campaignId', campaignId);
    const base = `${window.location.origin}${path}`;

    let ref = invite.referral_code;
    try {
        const inviteUrl = new URL(invite.referral_link);
        ref = inviteUrl.searchParams.get('ref') ?? inviteUrl.searchParams.get('referral_code') ?? invite.referral_code;
    } catch {
        // use referral_code fallback
    }

    const url = new URL(base);
    if (ref) url.searchParams.set('ref', ref);
    return url.toString();
}

export function setPendingCampaignReferralCopy(campaignId: string): void {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(PENDING_CAMPAIGN_REFERRAL_COPY_KEY, campaignId);
}

export function consumePendingCampaignReferralCopy(expectedCampaignId?: string): boolean {
    if (typeof sessionStorage === 'undefined') return false;
    const id = sessionStorage.getItem(PENDING_CAMPAIGN_REFERRAL_COPY_KEY);
    if (!id) return false;
    if (expectedCampaignId && id !== expectedCampaignId) return false;
    sessionStorage.removeItem(PENDING_CAMPAIGN_REFERRAL_COPY_KEY);
    return true;
}
